import { EntityType } from '@ftr/contracts/api/permissions'
import {
  CreatePermissionOnGroup,
  RestrictedModel,
  UserGroupPermission,
  UserGroupPermissionId,
} from '@ftr/contracts/api/user-group'
import { Uuid } from '@ftr/contracts/type/shared/Uuid'
import { BiSetMultiMap, MapUtils } from '@ftr/foundation'

export interface UserGroupPermissionState {
  isEnabled: boolean
  restrictions: Map<EntityType, Set<Uuid>>
  accessibleUnlinked: Set<EntityType>
}

/**
 * Saying "X" permissions, *depends on* "Y" permission
 *
 * If saving X permission, will also add Y permission.
 * If removing "Y" permission, will also remove X
 *
 * Important: This "Graph" must be Acyclic
 */
const DEPENDANT_PERMISSIONS = new BiSetMultiMap<UserGroupPermissionId, UserGroupPermissionId>([
  [UserGroupPermissionId.ReadLogSheets, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.ReadStt, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.RequestStt, UserGroupPermissionId.ReadStt],
  [UserGroupPermissionId.SealContent, UserGroupPermissionId.AccessSealedContent],
  [UserGroupPermissionId.AccessSealedContent, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.ManageRealtimeRecorders, UserGroupPermissionId.ViewRealTimeRecorders],
  [UserGroupPermissionId.TranscriberReadStt, UserGroupPermissionId.TranscribeRecordings],
  [UserGroupPermissionId.TranscriberReadLogSheets, UserGroupPermissionId.TranscribeRecordings],
  [UserGroupPermissionId.ShareRecordings, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.DownloadBundles, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.AccessOffTheRecordContent, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.ManageWhenCourtroomIsOnTheRecord, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.CaseInformation, UserGroupPermissionId.PlaybackRecordings],
  [UserGroupPermissionId.ManagePastSessions, UserGroupPermissionId.AccessOffTheRecordContent],
])

export type CurrentStage = 'staged' | 'committed'

/**
 * There are 2 sets of restrictions here...
 *
 * Committed = "Will be sent to the server when the save button is pressed"
 * Staged = "Will be moved to committed when the 'Set Access' button is pressed
 *
 * Note:
 * Adding "Restrictions" are always "Staged".
 * Enabling permissions always goes to "Committed"
 * Setting access moves "Staged" to "Committed"
 */
export class UserGroupPermissionStateMap {
  /**
   * The permissions that will be saved back to the server
   */
  private committed = new Map<UserGroupPermissionId, UserGroupPermissionState>()

  /**
   * The current changes to restrictions which can either be discarded (via resetRestrictions) or committed (via
   * commitChanges)
   */
  private staged = new Map<UserGroupPermissionId, UserGroupPermissionState>()

  /**
   * Which permissions can be restricted by which entity types.
   *
   * This allows us to recognise which permissions are misconfigured.
   *
   * If a permission is restricted by location, it must always have at least 1 location specified in order to hit
   * the save button
   */
  restrictedPermissions: Map<UserGroupPermissionId, Set<EntityType>>

  constructor(permissions: UserGroupPermission[], restrictedPermissions: Map<UserGroupPermissionId, Set<EntityType>>) {
    this.restrictedPermissions = restrictedPermissions
    const groupedById = MapUtils.buildFromValues(permissions, permission => permission.id)
    this.committed = MapUtils.mapValues(groupedById, perms => this.buildUserGroupPermissionState(perms))
    this.resetRestrictions()
  }

  addRestriction(
    permissionId: UserGroupPermissionId,
    entityType: EntityType,
    entityId: Uuid,
  ): UserGroupPermissionStateMap {
    const restrictions = this.getEntityIds(permissionId, entityType, 'staged')
    restrictions.add(entityId)

    return this
  }

  removeRestriction(
    permissionId: UserGroupPermissionId,
    entityType: EntityType,
    entityId: Uuid,
  ): UserGroupPermissionStateMap {
    const restrictions = this.getEntityIds(permissionId, entityType, 'staged')
    restrictions.delete(entityId)

    return this
  }

  setEnabled(
    permissionId: UserGroupPermissionId,
    isEnabled: boolean,
    stage: CurrentStage,
  ): UserGroupPermissionStateMap {
    const state = this.getOrCreate(permissionId, stage)
    state.isEnabled = isEnabled

    if (state.isEnabled) {
      DEPENDANT_PERMISSIONS.get(permissionId).forEach(p => this.setEnabled(p, true, stage))
    } else {
      DEPENDANT_PERMISSIONS.getInverse(permissionId).forEach(p => this.setEnabled(p, false, stage))
    }

    return this
  }

  isPermissionRestrictedBy(
    permissionId: UserGroupPermissionId,
    entityType: EntityType,
    entityId: Uuid,
    stage: CurrentStage,
  ): boolean {
    const entityIds = this.getEntityIds(permissionId, entityType, stage)
    return entityIds.has(entityId)
  }

  getRestrictionCountPerEntity(permission: UserGroupPermissionId, stage: CurrentStage): Map<EntityType, number> {
    const enabledEntityTypes = [...(this.restrictedPermissions.get(permission) || new Set())]
    return MapUtils.buildFromKeys(enabledEntityTypes, entityType =>
      this.getCountByEntityType(permission, entityType, stage),
    )
  }

  private getCountByEntityType(permission: UserGroupPermissionId, entityType: EntityType, stage: CurrentStage): number {
    const restrictionsForEntity = this.getRestrictions(permission, stage).get(entityType) || new Set()
    return restrictionsForEntity.size
  }

  /**
   * Used for the api call to save permissions
   */
  getCreatePermissionOnGroupModels(): CreatePermissionOnGroup[] {
    const models: CreatePermissionOnGroup[] = []
    for (const [permissionId, state] of this.committed) {
      // Enable/disable the permission (Also used for unrestricted permissions)
      if (state.isEnabled) {
        models.push(new CreatePermissionOnGroup(permissionId))
      }
      // Add all the restrictions
      for (const [entityType, entityIdSet] of state.restrictions) {
        const entityIdArray = [...entityIdSet]
        const createPermissionOnGroups = entityIdArray.map(
          entityId => new CreatePermissionOnGroup(permissionId, entityType, entityId),
        )
        models.push(...createPermissionOnGroups)
      }

      // Add unlinking
      for (const entityType of state.accessibleUnlinked) {
        models.push(new CreatePermissionOnGroup(permissionId, entityType, undefined))
      }
    }

    return models
  }

  // Creates the entity type key in the map if not specified
  getEntityIds(permissionId: UserGroupPermissionId, entityType: EntityType, stage: CurrentStage): Set<Uuid> {
    const state = this.getOrCreate(permissionId, stage)
    if (!state.restrictions.has(entityType)) {
      state.restrictions.set(entityType, new Set())
    }
    return state.restrictions.get(entityType)!
  }

  isPermissionEnabled(permissionId: UserGroupPermissionId): boolean {
    const state = this.getOrCreate(permissionId, 'committed')
    return state.isEnabled
  }

  /**
   * All enabled permissions must have locations restricted.
   *
   * Departments are optional, as you can give someone access to any resource without a department.
   *
   * Start with false, and if any permission is both enabled and misconfigured, return true.
   */
  areRestrictionsMisconfigured(): boolean {
    return MapUtils.reduce(
      this.committed,
      (acc, [permissionId, permissionState]) =>
        acc || (permissionState.isEnabled && !this.isPermissionConfigured(permissionId)),
      false as boolean,
    )
  }

  /**
   * All enabled permissions must have locations restricted.
   *
   * Departments are optional, as you can give someone access to any resource without a department.
   *
   */
  isPermissionConfigured(permissionId: UserGroupPermissionId): boolean {
    const requiresRestrictions = this.requiresRestrictionByLocation(permissionId)

    if (requiresRestrictions) {
      return this.getEntityIds(permissionId, 'location', 'committed').size > 0
    }

    return true
  }

  resetRestrictions(): void {
    this.staged = this.deepCopy(this.committed)
  }

  // This copies over the values using a deep copy.
  // Staged can remain the same, as its now identical to committed
  commitRestrictions(): void {
    this.committed = this.deepCopy(this.staged)
  }

  removeAllRestrictions(permissionId: UserGroupPermissionId, entityType: EntityType): void {
    const state = this.getOrCreate(permissionId, 'staged')
    state.restrictions.set(entityType, new Set())
    state.accessibleUnlinked.delete(entityType)

    DEPENDANT_PERMISSIONS.getInverse(permissionId).forEach(permission =>
      this.removeAllRestrictions(permission, entityType),
    )
  }

  propagateStagedRestrictions(permissionId: UserGroupPermissionId): boolean {
    let performedChanges = false
    const currentStaged = this.getRestrictions(permissionId, 'staged')

    Array.from(currentStaged.entries()).forEach(([entityType, currentRestrictions]) => {
      // Going up the dependency chain (i.e. this permission being updated is dependent on other permissions)
      DEPENDANT_PERMISSIONS.get(permissionId).forEach(parentPermissionId => {
        const parentStaged = this.getEntityIds(parentPermissionId, entityType, 'staged')

        if (parentStaged) {
          let performedParentChanges = false
          currentRestrictions.forEach(entityId => {
            if (!parentStaged.has(entityId)) {
              // Permission changes propogated up are additive (If I can do it, my ancestors should also be allowed to do it)
              this.addRestriction(parentPermissionId, entityType, entityId)
              performedParentChanges = true
            }
          })

          if (performedParentChanges) {
            this.propagateStagedRestrictions(parentPermissionId)
          }
          performedChanges = performedParentChanges || performedChanges
        }
      })

      // Going down the dependency chain (i.e. these other permissions are dependent on the one being updated)
      DEPENDANT_PERMISSIONS.getInverse(permissionId).forEach(childPermissionId => {
        const childStaged = this.getEntityIds(childPermissionId, entityType, 'staged')

        if (childStaged) {
          let performedChildChanges = false
          childStaged.forEach(entityId => {
            if (!currentRestrictions.has(entityId)) {
              // Permission changes propogated down are restrictive (If I can't do it, my descendents shouldn't do it either)
              this.removeRestriction(childPermissionId, entityType, entityId)
              performedChildChanges = true
            }
          })
          if (performedChildChanges) {
            this.propagateStagedRestrictions(childPermissionId)
          }
          performedChanges = performedChildChanges || performedChanges
        }
      })
    })
    return performedChanges
  }

  canAccessUnlinkedEntity(permission: UserGroupPermissionId, entityType: EntityType, stage: CurrentStage): boolean {
    return this.getOrCreate(permission, stage).accessibleUnlinked.has(entityType)
  }

  setUnlinkedAccess(permissionId: UserGroupPermissionId, entityType: EntityType, selected: boolean): void {
    const accessibleUnlinked = this.getAccessibleUnlinked(permissionId, 'staged')
    if (selected) {
      accessibleUnlinked.add(entityType)
    } else {
      accessibleUnlinked.delete(entityType)
    }
  }

  getAccessibleUnlinked(permissionId: UserGroupPermissionId, stage: CurrentStage): Set<EntityType> {
    return this.getOrCreate(permissionId, stage).accessibleUnlinked
  }

  private deepCopy(
    map: Map<UserGroupPermissionId, UserGroupPermissionState>,
  ): Map<UserGroupPermissionId, UserGroupPermissionState> {
    return MapUtils.mapValues(map, outer => ({
      isEnabled: outer.isEnabled,
      restrictions: MapUtils.mapValues(outer.restrictions, inner => new Set([...inner])),
      accessibleUnlinked: new Set([...outer.accessibleUnlinked]),
    }))
  }

  private getOrCreate(permissionId: UserGroupPermissionId, stage: CurrentStage): UserGroupPermissionState {
    const mapToModify = stage === 'committed' ? this.committed : this.staged
    if (!mapToModify.has(permissionId)) {
      mapToModify.set(permissionId, {
        isEnabled: false,
        restrictions: new Map(),
        accessibleUnlinked: new Set(),
      })
    }
    return mapToModify.get(permissionId)!
  }

  private buildUserGroupPermissionState(permission: UserGroupPermission): UserGroupPermissionState {
    return {
      isEnabled: permission.isEnabled,
      restrictions: this.buildRestrictionMap(permission.models || []),
      accessibleUnlinked: this.buildAccessibleUnlinked(permission),
    }
  }

  /**
   * Which entities are accessible unlinked. only used when populating
   */
  private buildAccessibleUnlinked(permission: UserGroupPermission): Set<EntityType> {
    const set = new Set(
      permission.models
        ?.filter(m => m.restrictedModelId === null && m.restrictedModelType !== undefined)
        .map(model => model.restrictedModelType),
    )
    return set || new Set()
  }

  /**
   * Whether or not the permissions requires at least 1 location id to be configured
   */
  private requiresRestrictionByLocation(permissionId: UserGroupPermissionId): boolean {
    const restrictions = this.restrictedPermissions.get(permissionId) || new Set()
    return restrictions.has('location')
  }

  private buildRestrictionMap(restrictedModels: RestrictedModel[]): Map<EntityType, Set<Uuid>> {
    return MapUtils.buildGroupedSetMultimap(
      restrictedModels.filter(model => model.restrictedModelId !== null),
      restrictedModel => restrictedModel.restrictedModelType!,
      restrictedModel => restrictedModel.restrictedModelId!,
    )
  }

  private getRestrictions(permission: UserGroupPermissionId, stage: CurrentStage): Map<EntityType, Set<Uuid>> {
    return this.getOrCreate(permission, stage).restrictions
  }
}
